import React from 'react';
import { Modal } from 'react-bootstrap';
import { getDatabase, ref, get } from 'firebase/database';
import { BaseComponentImpl } from '../types/implementations';
import ComponentDetailsView from '../Pages/ComponentPage/ComponentDetailsView';
import { Building } from '../types/protocols';

const COLORS = {
    primaryDark: '#369ec3',
};

interface Activity {
    componentKey: string;
    componentName: string;
    componentType: string;
    buildingKey?: string;
    buildingName?: string;
    activityType: 'replacement' | 'maintenance';
    activityName: string;
    templateId?: string;
    frequency: number;
    frequencyType: any;
    cost: number;
    costCalculation: string;
    baseType: string;
    nextDate?: string;
    // Inflation adjustment properties
    originalCost?: number;
    inflationYears?: number;
    inflationMultiplier?: number;
    priceSettingYear?: number;
}

interface MaintenanceActivityListProps {
    activities: Activity[];
    showCostCalculation?: boolean;
    adjustForInflation?: boolean;
    showBuildingName?: boolean;
}

const MaintenanceActivityList: React.FC<MaintenanceActivityListProps> = ({ activities, showCostCalculation = true, adjustForInflation = false, showBuildingName = false }) => {
    const [hoveredIndex, setHoveredIndex] = React.useState<number | null>(null);
    const [selectedComponent, setSelectedComponent] = React.useState<{ component: BaseComponentImpl, building: Building, buildingKey: string } | null>(null);
    const [loadingComponent, setLoadingComponent] = React.useState<boolean>(false);
    const [modalHiddenForFancybox, setModalHiddenForFancybox] = React.useState<boolean>(false);

    const handleComponentClick = async (activity: Activity) => {
        if (!activity.buildingKey || !activity.componentKey) {
            console.error('Missing buildingKey or componentKey', activity);
            return;
        }

        setLoadingComponent(true);
        try {
            const db = getDatabase();
            const [summarySnapshot, modelSnapshot] = await Promise.all([
                get(ref(db, `buildingsSummary/${activity.buildingKey}`)),
                get(ref(db, `buildings/${activity.buildingKey}`))
            ]);
            
            if (summarySnapshot.exists() || modelSnapshot.exists()) {
                const building: Building = {
                    key: activity.buildingKey,
                    summary: summarySnapshot.exists() ? summarySnapshot.val() : { key: activity.buildingKey },
                    model: modelSnapshot.exists() ? modelSnapshot.val() : { key: activity.buildingKey }
                };
                
                const componentData = building.model.structure?.components?.[activity.componentKey];
                
                if (componentData) {
                    const component = new BaseComponentImpl(componentData);
                    setSelectedComponent({ 
                        component, 
                        building,
                        buildingKey: activity.buildingKey
                    });
                } else {
                    console.error('Component not found:', activity.componentKey);
                }
            } else {
                console.error('Building not found:', activity.buildingKey);
            }
        } catch (error) {
            console.error('Error loading component:', error);
        } finally {
            setLoadingComponent(false);
        }
    };

    return (
        <div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <tbody>
                    {activities.map((activity, index) => (
                        <tr 
                            key={index} 
                            style={{ 
                                borderBottom: '1px solid #e0e0e0',
                                backgroundColor: hoveredIndex === index ? '#fafafa' : 'transparent',
                                transition: 'background-color 0.15s ease'
                            }}
                            onMouseEnter={() => setHoveredIndex(index)}
                            onMouseLeave={() => setHoveredIndex(null)}
                        >
                            <td style={{ padding: '10px' }}>
                                {activity.activityName}
                            </td>
                            {showBuildingName && (
                                <td style={{ padding: '10px' }}>
                                    {activity.buildingKey ? (
                                        <a
                                            href={`/building/${activity.buildingKey}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{
                                                color: COLORS.primaryDark,
                                                textDecoration: 'underline',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            {activity.buildingName || 'Okänd byggnad'}
                                        </a>
                                    ) : (
                                        <span>{activity.buildingName || 'Okänd byggnad'}</span>
                                    )}
                                </td>
                            )}
                            <td 
                                style={{ 
                                    padding: '10px',
                                    cursor: loadingComponent ? 'wait' : 'pointer',
                                    color: COLORS.primaryDark,
                                    textDecoration: 'underline'
                                }}
                                onClick={() => handleComponentClick(activity)}
                            >
                                {activity.componentName}
                            </td>
                            <td style={{ padding: '10px' }}>
                                {activity.nextDate}
                            </td>
                            <td 
                                style={{ 
                                    padding: '10px', 
                                    textAlign: 'right',
                                    cursor: showCostCalculation && activity.costCalculation ? 'help' : 'default'
                                }}
                                title={showCostCalculation && activity.costCalculation ? activity.costCalculation : undefined}
                            >
                                {activity.cost > 0 ? (
                                    <strong>{Math.round(activity.cost).toLocaleString('sv-SE')} kr</strong>
                                ) : (
                                    <span>Ingen kostnad</span>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            <Modal 
                show={!!selectedComponent && !modalHiddenForFancybox} 
                onHide={() => setSelectedComponent(null)}
                size="lg"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        {selectedComponent && selectedComponent.component.displayName(selectedComponent.building)}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {loadingComponent ? (
                        <div style={{ textAlign: 'center', padding: '40px' }}>
                            <div>Laddar komponent...</div>
                        </div>
                    ) : selectedComponent ? (
                        <ComponentDetailsView
                            component={selectedComponent.component}
                            building={selectedComponent.building}
                            buildingKey={selectedComponent.buildingKey}
                            showHeader={false}
                            containerStyle={{
                                backgroundColor: 'transparent',
                                borderRadius: '0',
                                padding: '0',
                                boxShadow: 'none'
                            }}
                            onFancyboxOpen={() => setModalHiddenForFancybox(true)}
                            onFancyboxClose={() => setModalHiddenForFancybox(false)}
                        />
                    ) : null}
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default MaintenanceActivityList;
